import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { FadeUp } from "../components/Reveal";
import { CONTACT } from "../data/content";

const DOCS = {
  "privacy-policy": {
    title: "Privacy Policy",
    sections: [
      {
        h: "Information We Collect",
        p: "When you submit an enquiry through this website, we collect your name, mobile number, email address and the travel preferences you share with us. This information is used solely to respond to your enquiry and plan your journey.",
      },
      {
        h: "How We Use It",
        p: "Your details are used only by the HIDDENBIOMES travel team to contact you and design your itinerary. We do not sell, rent or share your personal information with third parties for marketing purposes.",
      },
      {
        h: "Data Security",
        p: "Enquiry data is stored securely and accessed only by authorised team members. You may request deletion of your information at any time by writing to us.",
      },
      {
        h: "Contact",
        p: `For any privacy-related questions, write to ${CONTACT.email} or call ${CONTACT.phone}.`,
      },
    ],
  },
  "terms-conditions": {
    title: "Terms & Conditions",
    sections: [
      {
        h: "Enquiries & Bookings",
        p: "Submitting an enquiry through this website does not constitute a confirmed booking. All journeys are confirmed personally by our travel team after discussing availability, inclusions and payment terms with you.",
      },
      {
        h: "Pricing",
        p: "Prices for tours and vehicle rentals are provided on request and depend on travel dates, group size, accommodation category and seasonal availability.",
      },
      {
        h: "Safari Permits",
        p: "Entry to national parks and tiger reserves is subject to forest department permit availability. We recommend enquiring well in advance, especially for peak season travel.",
      },
      {
        h: "Traveller Responsibility",
        p: "Travellers are required to carry valid government photo identification for park entry and hotel check-in, and to follow all park rules and naturalist instructions during wildlife activities.",
      },
    ],
  },
  "cancellation-policy": {
    title: "Cancellation Policy",
    sections: [
      {
        h: "Before Confirmation",
        p: "Enquiries can be withdrawn at any time before booking confirmation at no charge. No payment is collected through this website.",
      },
      {
        h: "After Confirmation",
        p: "Once a journey is confirmed, cancellation terms depend on the hotels, lodges and safari permits booked for your itinerary. Our team will clearly share the applicable terms with you before you confirm.",
      },
      {
        h: "Safari Permits",
        p: "Forest department safari permits, once issued, are generally non-refundable and non-transferable as per official park regulations.",
      },
      {
        h: "Speak to Us",
        p: `Plans change — we understand. Call ${CONTACT.phone} or write to ${CONTACT.email} and we will always find the fairest possible way forward.`,
      },
    ],
  },
};

export default function LegalPage() {
  const { doc } = useParams();
  const page = DOCS[doc];
  if (!page) return <Navigate to="/" replace />;

  return (
    <main data-testid="legal-page" className="bg-forest-950 min-h-screen pt-32 pb-24">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <FadeUp>
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-cream/60 hover:text-gold transition-colors"
          >
            <ArrowLeft size={13} /> Back to Home
          </Link>
          <div className="flex items-center gap-4 mt-10 mb-4">
            <span className="h-px w-12 bg-gold/40" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">
              HiddenBiomes LLP
            </span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-cream tracking-tight">{page.title}</h1>
        </FadeUp>
        <div className="mt-12 space-y-10">
          {page.sections.map((s, i) => (
            <FadeUp key={s.h} delay={i * 0.06}>
              <div className="border-l border-gold/25 pl-6">
                <h2 className="font-serif text-xl text-cream">{s.h}</h2>
                <p className="text-sm text-sage leading-relaxed mt-3">{s.p}</p>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.2}>
          <p className="font-mono text-[10px] tracking-[0.15em] text-fog mt-16">
            © 2026 HIDDENBIOMES LLP · GSTIN {CONTACT.gstin} · Indore, Madhya Pradesh
          </p>
        </FadeUp>
      </div>
    </main>
  );
}
